import React from 'react';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';

import Row from '@Components/layout/Row';
import Tabs from '@Components/layout/Tabs/Tabs';
import Typography from '@Components/Typography';
import { Messages } from '@Config/messages';
import { RouteConfig } from '@Config/routes';
import { RegisterUserInput } from '@Graphql/graphqlTypes.generated';
import { useTranslations } from '@Hooks/useTranslations';
import styles from '@Routes/auth/AuthenticationPage/AuthenticationPage.module.scss';
import LoginForm from '@Routes/auth/AuthenticationPage/LoginForm';
import RegisterForm from '@Routes/auth/AuthenticationPage/RegisterForm';
import { RegisterFormData } from '@Routes/auth/AuthenticationPage/RegisterForm/RegisterForm';
import { login, register } from '@Store/auth/auth.actions';
import { LoginActionData, LoginFormData } from '@Store/auth/auth.types';
import { bindActionToPromise } from '@Utils/redux';
import { LocationReferer } from '@Utils/types';

interface AuthenticationFormProps extends LocationReferer {
  referer?: string;
  registerInitialValues: RegisterFormData;
  loginInitialValues: LoginFormData;
  onTabChange?: (activeTab: number) => void;
  initialTab?: number;
}

enum AuthenticationFormTabTypes {
  login = 'login',
  register = 'register',
}

const AuthenticationForm: React.FC<AuthenticationFormProps> = ({
  referer,
  loginInitialValues,
  registerInitialValues,
  onTabChange,
  initialTab = 0,
}) => {
  const t = useTranslations();
  const dispatch = useDispatch();

  const loginAction = bindActionToPromise<LoginActionData>(dispatch, login.started);
  const registerAction = bindActionToPromise<RegisterUserInput>(dispatch, register.started);

  return (
    <Tabs
      onTabChange={onTabChange}
      initialTab={initialTab}
      tabs={[
        <Typography tag="span" key={AuthenticationFormTabTypes.login} msg={t(Messages.loginFormLogin)} />,
        <Typography tag="span" key={AuthenticationFormTabTypes.register} msg={t(Messages.registerFormRegister)} />,
      ]}
      tabContents={[
        <>
          <LoginForm initialValues={loginInitialValues} key={0} referer={referer} onSubmit={loginAction} />
          <Row constant justifyCenter className={styles.passwordResetRedirect}>
            <Link to={RouteConfig.ForgotPassword.buildLink()}>
              <Typography tag="span" msg={t(Messages.loginFormForgotPassword)} />
            </Link>
          </Row>
        </>,
        <RegisterForm key={1} onSubmit={registerAction} initialValues={registerInitialValues} />,
      ]}
    />
  );
};

export default AuthenticationForm;
